import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { LoadingState, EmptyState } from '../../components/ui/Spinner';
import { formatAmount } from '../../lib/format';

interface ProfitReportProps {
  entityId: string;
  periodId: string;
}

interface DayData {
  rate: number;
  spread: number;
  profit: number;
  volume: number;
}

interface PairData {
  base: string;
  quote: string;
  label: string;
  pairKey: string;
  totalProfit: number;
  totalVolume: number;
  buy: Record<string, DayData>;
  sell: Record<string, DayData>;
}

interface ProfitDailyData {
  dates: string[];
  pairs: PairData[];
  totals: { currency: string; total: string }[];
  period: { start: string; end: string };
}

export default function ProfitReport({ entityId, periodId }: ProfitReportProps) {
  const { t } = useTranslation('reports');
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = (searchParams.get('viewMode') as 'summary' | 'daily') || 'daily';
  const days = parseInt(searchParams.get('days') || '14', 10) || 14;

  const setViewMode = useCallback((mode: 'summary' | 'daily') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (mode === 'daily') next.delete('viewMode'); else next.set('viewMode', mode);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setDays = useCallback((d: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (d === 14) next.delete('days'); else next.set('days', String(d));
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const periodParam = periodId ? `&periodId=${periodId}` : '';

  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['report-profit', entityId, periodId],
    queryFn: () => api.reports.profit(entityId, periodId ? { periodId } : undefined),
    enabled: viewMode === 'summary',
  });

  const { data: dailyData, isLoading: dailyLoading } = useQuery<ProfitDailyData>({
    queryKey: ['report-profit-daily', entityId, periodId, days],
    queryFn: async () => {
      const res = await fetch(`/api/reports/profit?daily=true&days=${days}${periodParam}`, {
        headers: { 'X-Entity-Id': entityId },
        credentials: 'include',
      });
      return res.json();
    },
    enabled: viewMode === 'daily',
  });

  const isLoading = viewMode === 'summary' ? summaryLoading : dailyLoading;

  if (isLoading) return <LoadingState message={t('profit.loading')} />;

  return (
    <div className="report-content">
      <div className="card mb-4">
        <div className="controls-row">
          <div className="view-toggle">
            <button
              className={`btn ${viewMode === 'daily' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('daily')}
            >
              {t('views.daily')}
            </button>
            <button
              className={`btn ${viewMode === 'summary' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('summary')}
            >
              {t('views.summary')}
            </button>
          </div>
          {viewMode === 'daily' && (
            <select
              className="form-input"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              style={{ width: 'auto' }}
            >
              <option value="7">7 {t('balances.days')}</option>
              <option value="14">14 {t('balances.days')}</option>
              <option value="30">30 {t('balances.days')}</option>
              <option value="90">90 {t('balances.days')}</option>
            </select>
          )}
        </div>
      </div>

      {viewMode === 'daily' ? (
        <DailyProfitTable data={dailyData} />
      ) : (
        <SummaryProfit data={summaryData} />
      )}

      <style>{`
        .controls-row { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; }
        .view-toggle { display: flex; gap: 8px; }
      `}</style>
    </div>
  );
}

function DailyProfitTable({ data }: { data: ProfitDailyData | undefined }) {
  const { t } = useTranslation('reports');

  if (!data?.dates?.length || !data?.pairs?.length) {
    return (
      <div className="card">
        <EmptyState title={t('profit.empty.title')} description={t('profit.empty.description')} />
      </div>
    );
  }

  // Format date for column header (e.g., "15 Dec" or "15 дек")
  const formatDateHeader = (dateStr: string) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const month = d.toLocaleDateString(undefined, { month: 'short' });
    return `${day} ${month}`;
  };

  // Currency pair icons
  const pairIcons: Record<string, string> = {
    'EUR/USDT': '💶🪙',
    'EUR/USD': '💶💵',
    'EUR/RUB': '💶₽',
    'USDT/USDC': '🪙🪙',
    'USD/USDT': '💵🪙',
    'USD/RUB': '💵₽',
  };

  return (
    <div className="card">
      <div className="profit-table-wrapper">
        <table className="profit-table">
          <thead>
            <tr>
              <th className="header-pair" rowSpan={2}>{t('profit.title')}</th>
              <th className="header-direction" rowSpan={2}></th>
              {data.dates.map((date) => (
                <th key={date} colSpan={4} className="header-date-group">
                  {formatDateHeader(date)}
                </th>
              ))}
            </tr>
            <tr>
              {data.dates.map((date) => (
                <>
                  <th key={`${date}-rate`} className="header-metric">{t('profit.rate')}</th>
                  <th key={`${date}-spread`} className="header-metric">{t('profit.spread')}</th>
                  <th key={`${date}-profit`} className="header-metric">{t('columns.profit')}</th>
                  <th key={`${date}-volume`} className="header-metric">{t('columns.volume')}</th>
                </>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.pairs.map((pair) => (
              <>
                {/* Pair Header */}
                <tr key={`header-${pair.pairKey}`} className="pair-header">
                  <td colSpan={2 + data.dates.length * 4}>
                    <span className="pair-icon">{pairIcons[pair.pairKey] || '💱'}</span>
                    <span className="pair-label">{pair.label}</span>
                    <span className="pair-key">({pair.pairKey})</span>
                  </td>
                </tr>
                {/* BUY Row */}
                <tr key={`${pair.pairKey}-buy`}>
                  <td className="cell-pair-name"></td>
                  <td className="cell-direction cell-buy">BUY</td>
                  {data.dates.map((date) => {
                    const d = pair.buy[date] || { rate: 0, spread: 0, profit: 0, volume: 0 };
                    return (
                      <>
                        <td key={`${date}-rate`} className="cell-value">
                          {d.rate > 0 ? d.rate.toFixed(4) : '-'}
                        </td>
                        <td key={`${date}-spread`} className="cell-value">
                          {d.spread > 0 ? `${d.spread.toFixed(2)}%` : '-'}
                        </td>
                        <td key={`${date}-profit`} className={`cell-value ${d.profit > 0 ? 'text-success' : d.profit < 0 ? 'text-error' : ''}`}>
                          {d.profit !== 0 ? formatAmount(d.profit.toString()) : '-'}
                        </td>
                        <td key={`${date}-volume`} className="cell-value">
                          {d.volume > 0 ? formatAmount(d.volume.toString()) : '-'}
                        </td>
                      </>
                    );
                  })}
                </tr>
                {/* SELL Row */}
                <tr key={`${pair.pairKey}-sell`}>
                  <td className="cell-pair-name"></td>
                  <td className="cell-direction cell-sell">SELL</td>
                  {data.dates.map((date) => {
                    const d = pair.sell[date] || { rate: 0, spread: 0, profit: 0, volume: 0 };
                    return (
                      <>
                        <td key={`${date}-rate`} className="cell-value">
                          {d.rate > 0 ? d.rate.toFixed(4) : '-'}
                        </td>
                        <td key={`${date}-spread`} className="cell-value">
                          {d.spread > 0 ? `${d.spread.toFixed(2)}%` : '-'}
                        </td>
                        <td key={`${date}-profit`} className={`cell-value ${d.profit > 0 ? 'text-success' : d.profit < 0 ? 'text-error' : ''}`}>
                          {d.profit !== 0 ? formatAmount(d.profit.toString()) : '-'}
                        </td>
                        <td key={`${date}-volume`} className="cell-value">
                          {d.volume > 0 ? formatAmount(d.volume.toString()) : '-'}
                        </td>
                      </>
                    );
                  })}
                </tr>
              </>
            ))}

            {/* Totals Section */}
            {data.totals.length > 0 && (
              <>
                <tr className="totals-header">
                  <td colSpan={2 + data.dates.length * 4}>{t('balances.totals')}</td>
                </tr>
                {data.totals.map((tot) => (
                  <tr key={`total-${tot.currency}`} className="totals-row">
                    <td className="cell-pair-name">{tot.currency}</td>
                    <td className="cell-direction"></td>
                    <td colSpan={data.dates.length * 4} className="cell-total-amount">
                      <span className={parseFloat(tot.total) >= 0 ? 'text-success' : 'text-error'}>
                        {parseFloat(tot.total) >= 0 ? '+' : ''}{formatAmount(tot.total)}
                      </span>
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .profit-table-wrapper {
          overflow-x: auto;
          overflow-y: visible;
        }

        .profit-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
          white-space: nowrap;
        }

        .profit-table th,
        .profit-table td {
          border: 1px solid var(--color-border);
          padding: 4px 6px;
        }

        .profit-table thead th {
          background: var(--color-table-header);
          font-weight: 600;
          text-align: center;
        }

        .header-pair {
          text-align: left !important;
          min-width: 150px;
        }

        .header-direction {
          min-width: 50px;
        }

        .header-date-group {
          background: #e3f2fd !important;
          border-bottom: none;
        }

        .header-metric {
          font-size: 10px;
          min-width: 60px;
          background: #f5f5f5 !important;
        }

        .pair-header {
          background: #fff3e0 !important;
          font-weight: 600;
        }

        .pair-header td {
          padding: 6px 10px;
        }

        .pair-icon {
          margin-right: 8px;
        }

        .pair-label {
          margin-right: 8px;
        }

        .pair-key {
          color: var(--color-text-muted);
          font-weight: 400;
        }

        .cell-pair-name {
          padding-left: 16px !important;
        }

        .cell-direction {
          text-align: center;
          font-weight: 600;
          font-size: 10px;
        }

        .cell-buy {
          color: #2e7d32;
          background: #e8f5e9;
        }

        .cell-sell {
          color: #c62828;
          background: #ffebee;
        }

        .cell-value {
          text-align: right;
          font-family: monospace;
        }

        .totals-header {
          background: #e8eaf6 !important;
          font-weight: 600;
        }

        .totals-header td {
          padding: 6px 10px;
        }

        .totals-row {
          background: #f3e5f5;
        }

        .cell-total-amount {
          text-align: center;
          font-size: 14px;
          font-weight: 700;
          font-family: monospace;
        }

        .text-success {
          color: var(--color-success);
        }

        .text-error {
          color: var(--color-error);
        }

        /* Dark theme adjustments */
        :root[data-theme="dark"] .header-date-group {
          background: #1565c0 !important;
          color: white;
        }

        :root[data-theme="dark"] .header-metric {
          background: #37474f !important;
        }

        :root[data-theme="dark"] .pair-header {
          background: #5d4037 !important;
          color: white;
        }

        :root[data-theme="dark"] .cell-buy {
          background: rgba(46, 125, 50, 0.2);
        }

        :root[data-theme="dark"] .cell-sell {
          background: rgba(198, 40, 40, 0.2);
        }

        :root[data-theme="dark"] .totals-header {
          background: #311b92 !important;
          color: white;
        }

        :root[data-theme="dark"] .totals-row {
          background: rgba(49, 27, 146, 0.2);
        }
      `}</style>
    </div>
  );
}

function SummaryProfit({ data }: { data: any }) {
  const { t } = useTranslation('reports');

  if (!data?.byPair?.length) {
    return (
      <div className="card">
        <EmptyState title={t('profit.empty.title')} description={t('profit.empty.description')} />
      </div>
    );
  }

  return (
    <div className="report-content">
      {data?.totals?.length > 0 && (
        <div className="card mb-4">
          <h3 className="card-title mb-4">{t('profit.totalProfit')}</h3>
          <div className="profit-totals">
            {data.totals.map((tot: any) => (
              <div key={tot.currency} className="profit-total">
                <span className="profit-currency">{tot.currency}</span>
                <span className={`profit-amount ${parseFloat(tot.total) >= 0 ? 'positive' : 'negative'}`}>
                  {parseFloat(tot.total) >= 0 ? '+' : ''}{formatAmount(tot.total)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="card-title mb-4">{t('profit.byPair')}</h3>
        <table className="table">
          <thead>
            <tr>
              <th>{t('columns.pair')}</th>
              <th className="text-right">{t('columns.txCount')}</th>
              <th className="text-right">{t('columns.volume')}</th>
              <th className="text-right">{t('columns.avgRate')}</th>
              <th className="text-right">{t('columns.profit')}</th>
            </tr>
          </thead>
          <tbody>
            {data.byPair.map((p: any, i: number) => (
              <tr key={i}>
                <td><strong>{p.currencyPair}</strong></td>
                <td className="text-right">{p.transactionCount}</td>
                <td className="text-right font-mono">{formatAmount(p.totalIn)}</td>
                <td className="text-right font-mono">{p.avgRate ? parseFloat(p.avgRate).toFixed(4) : '-'}</td>
                <td className={`text-right font-mono ${p.totalProfit && parseFloat(p.totalProfit) >= 0 ? 'text-success' : 'text-error'}`}>
                  {p.totalProfit ? formatAmount(p.totalProfit) : '-'} {p.profitCurrency}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        .profit-totals { display: flex; gap: 32px; }
        .profit-total { display: flex; flex-direction: column; gap: 4px; }
        .profit-currency { font-size: 14px; color: var(--color-gray-500); }
        .profit-amount { font-size: 32px; font-weight: 600; font-family: monospace; }
        .profit-amount.positive { color: var(--color-success); }
        .profit-amount.negative { color: var(--color-error); }
        .font-mono { font-family: monospace; }
      `}</style>
    </div>
  );
}
